import { isEmpty } from 'lodash';
import { getTimezone } from '@shared/util/timezone';
import { clearPromptTokenCache, mintPromptToken } from './mintPromptToken';
import { AskPromptRequest, AskScope } from './types';

interface PostAgUiPromptOptions {
  businessId: number;
  url: string;
  /** Already-serialized `AskPromptRequest`, built by `CoastAskAgent.requestInit`. */
  body: string;
  signal?: AbortSignal;
}

export interface AgUiRequestBodyOptions {
  /** The wire prompt, directives already appended (see `withChatDirectives`). */
  prompt: string;
  scope: AskScope;
  /**
   * The gateway's id for this conversation. Omitted from the body when empty, which is how a
   * new conversation asks the gateway to start one — see `CoastAskAgent.adoptThreadId`.
   */
  threadId?: string;
}

/**
 * Builds the `/prompt` body. Org applies no narrowing, so `scope` is omitted for it, and
 * `protocol` names the only protocol there is.
 *
 * An empty `threadId` is left off the body rather than sent as `''`, so a first turn is
 * byte-identical to what the gateway received before multi-turn existed. Empty rather than
 * nil: the agent holds it as a required string (AG-UI's field), so "no thread yet" reaches
 * here as `''` rather than as nothing.
 *
 * The time zone is read here rather than passed in, so every request carries it without a
 * call site having to remember to; it is resolved per request because the browser's zone can
 * change under a long-lived session (travel, a DST-less device zone change).
 */
export function buildAgUiRequestBody({
  prompt,
  scope,
  threadId,
}: AgUiRequestBodyOptions): AskPromptRequest {
  const timezone = getTimezone();

  return {
    prompt,
    ...(isEmpty(threadId) ? {} : { threadId }),
    ...(scope.type === 'org' ? {} : { scope: { type: scope.type, id: scope.id } }),
    // A blank guess is left off rather than sent as `''`, which the gateway would read as a
    // zone rather than as the absence of one.
    ...(isEmpty(timezone) ? {} : { timezone }),
    protocol: 'ag-ui',
  };
}

async function post(accessToken: string, { url, body, signal }: PostAgUiPromptOptions) {
  return fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      authorization: `Bearer ${accessToken}`,
    },
    body,
    signal,
  });
}

/**
 * Mints (or reuses) a prompt token and POSTs an AG-UI prompt request, returning
 * the raw streaming `Response` — AG-UI's own SSE parser consumes the body, so
 * the stream is not read here. On a 401/403 (expired or wrong-scope token) it
 * forces a re-mint and retries once.
 */
export async function postAgUiPrompt(options: PostAgUiPromptOptions): Promise<Response> {
  const { businessId } = options;
  const accessToken = await mintPromptToken(businessId);
  const response = await post(accessToken, options);

  if (response.status !== 401 && response.status !== 403) {
    return response;
  }

  clearPromptTokenCache(businessId);
  const refreshedToken = await mintPromptToken(businessId, { forceRefresh: true });
  return post(refreshedToken, options);
}
